import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, mediaUrl, storePublicBase } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import { useFileUpload } from "@/lib/uploadProgress";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, PenLine, Plus, Trash2 } from "lucide-react";

const blank = () => ({ name: "", slug: "", photo_url: "", description: "", email: "" });

export default function AdminAuthors() {
  const { tenant } = useMerchantAuth();
  const uploadFile = useFileUpload();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/merchant/authors").then(({ data }) => {
      setItems(data.items || []);
      setLocked(false);
    }).catch((e) => {
      if (String(extractErr(e)).includes("not active")) setLocked(true);
      else toast.error(extractErr(e));
    });
  };
  useEffect(() => { load(); }, []);

  const pageUrl = (slug) => `${storePublicBase(tenant)}/author/${slug}`;
  const copyUrl = async (slug) => {
    try {
      await navigator.clipboard.writeText(pageUrl(slug));
      toast.success("Author link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };
  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      if (draft.id) await api.put(`/merchant/authors/${draft.id}`, draft);
      else await api.post("/merchant/authors", draft);
      toast.success(draft.id ? "Author saved" : "Author added");
      setDraft(null);
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.name}”? Products will no longer show this author.`)) return;
    try {
      await api.delete(`/merchant/authors/${row.id}`);
      toast.success("Author deleted");
      if (draft?.id === row.id) setDraft(null);
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const data = await uploadFile("/merchant/media/upload", fd);
      setDraft((d) => ({ ...d, photo_url: data.url || "" }));
    } catch (e) { toast.error(extractErr(e)); }
    finally { setUploading(false); }
  };

  if (locked) {
    return (
      <div className="max-w-xl" data-testid="admin-authors">
        <h1 className="font-display text-2xl font-bold">Authors</h1>
        <p className="text-sm text-slate-500 mt-2">Turn on Author Info &amp; Page in the App Store to add authors.</p>
        <Link to="/admin/addons" className="inline-block mt-3 text-sm underline">Open App Store</Link>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-4xl" data-testid="admin-authors">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-rose-700 text-xs font-semibold uppercase tracking-wide"><PenLine className="w-4 h-4" /> Author Info &amp; Page</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">Authors</h1>
          <p className="text-sm text-slate-500 mt-1">Each author gets a public page. Assign them while adding or editing a product.</p>
        </div>
        {!draft && (
          <Button type="button" data-testid="author-new" onClick={() => setDraft(blank())}><Plus className="w-4 h-4 mr-1" /> Add author</Button>
        )}
      </div>

      {draft && (
        <div className="rounded-2xl border bg-white p-4 sm:p-6 space-y-4" data-testid="author-editor">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-28 h-36 rounded-xl bg-slate-100 overflow-hidden shrink-0 border">
              {draft.photo_url ? <img src={mediaUrl(draft.photo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">Photo</div>}
            </div>
            <div className="flex-1 space-y-3">
              <label className="block text-xs font-semibold text-slate-500">Name
                <Input className="mt-1" data-testid="author-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </label>
              <label className="block text-xs font-semibold text-slate-500">Page address
                <Input className="mt-1" data-testid="author-slug" value={draft.slug} placeholder="auto from name" onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-blue-700 cursor-pointer">
                <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
                {uploading ? "Uploading…" : "Upload photo"}
              </label>
            </div>
          </div>
          <label className="block text-xs font-semibold text-slate-500">Description
            <textarea className="mt-1 w-full min-h-[120px] rounded-md border px-3 py-2 text-sm" data-testid="author-description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </label>
          <label className="block text-xs font-semibold text-slate-500">Contact email
            <Input className="mt-1" type="email" data-testid="author-email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button type="button" data-testid="author-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save author"}</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((row) => (
          <div key={row.id} className="rounded-xl border bg-white p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`author-row-${row.id}`}>
            <div className="w-12 h-14 rounded-lg bg-slate-100 overflow-hidden shrink-0">
              {row.photo_url ? <img src={mediaUrl(row.photo_url)} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{row.name}</div>
              <div className="text-xs text-slate-500 truncate">{pageUrl(row.slug)}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setDraft({ ...blank(), ...row })}>Edit</Button>
              <Button type="button" variant="outline" size="sm" data-testid={`author-copy-${row.id}`} onClick={() => copyUrl(row.slug)}><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>
              <Button type="button" variant="outline" size="sm" data-testid={`author-delete-${row.id}`} onClick={() => remove(row)}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
            </div>
          </div>
        ))}
        {items.length === 0 && !draft && <div className="rounded-xl border border-dashed p-8 text-center text-sm text-slate-500">No authors yet.</div>}
      </div>
    </div>
  );
}
